import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import StatusChip from "@/components/StatusChip";
import ShareTrackingButton from "@/components/ShareTrackingButton";
import { MagnifyingGlass, PlusCircle, PencilSimple, UserPlus, Copy } from "@phosphor-icons/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatDate, formatCurrency } from "@/lib/format";

const STATUS_OPTIONS = [
  { key: "all", label: "All statuses" },
  { key: "ORDER_RECEIVED", label: "Order Received" },
  { key: "RAW_MATERIALS", label: "Raw Materials" },
  { key: "IN_PRODUCTION", label: "In Production" },
  { key: "QUALITY_TESTING", label: "Quality Testing" },
  { key: "READY_FOR_SHIPMENT", label: "Ready for Shipment" },
  { key: "SHIPPED", label: "Shipped" },
  { key: "DELIVERED", label: "Delivered" },
];

const IN_PROD_STATUSES = ["RAW_MATERIALS", "IN_PRODUCTION", "QUALITY_TESTING"];
const SHIPPING_STATUSES = ["READY_FOR_SHIPMENT", "SHIPPED"];

export default function AdminDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/orders", { params });
      setOrders(res.data);
    } catch (e) {
      console.error("[AdminDashboard] fetchOrders failed:", e);
    }
    setLoading(false);
  }, [q, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, fetchOrders]);

  const kpis = useMemo(() => {
    const total = orders.length;
    const inProd = orders.filter((o) => IN_PROD_STATUSES.includes(o.current_status)).length;
    const shipping = orders.filter((o) => SHIPPING_STATUSES.includes(o.current_status)).length;
    const revenue = orders.reduce((s, o) => s + (o.total_price || 0), 0);
    return { total, inProd, shipping, revenue };
  }, [orders]);

  return (
    <Layout>
      <div className="mb-10">
        <div className="mt-tiny text-[#FF5A1F]">Factory Console</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-zinc-900 mt-2">
          Production Overview
        </h1>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-kpis">
        <KPI label="Total Orders" value={kpis.total} />
        <KPI label="In Production" value={kpis.inProd} />
        <KPI label="Shipping Pipeline" value={kpis.shipping} />
        <KPI label="Total Revenue" value={formatCurrency(kpis.revenue)} accent />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row gap-3 mb-6 md:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="admin-search-input"
            placeholder="Search order, client, product..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchOrders()}
            className="w-full pl-10 pr-4 py-3 bg-white border border-zinc-200 focus:border-zinc-900 outline-none transition-colors"
          />
        </div>
        <div className="w-full md:w-56">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="admin-status-filter" className="h-12 border-zinc-200 rounded-none bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          onClick={fetchOrders}
          data-testid="admin-search-button"
          className="bg-zinc-900 hover:bg-zinc-800 text-white px-5 py-3 font-medium transition-colors"
        >
          Search
        </button>
        <Link
          to="/admin/new"
          data-testid="admin-new-order-button"
          className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04814] text-white px-5 py-3 font-medium transition-colors"
        >
          <PlusCircle size={18} weight="fill" /> New Order
        </Link>
        <NewClientDialog />
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200" data-testid="admin-orders-table">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-200">
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Order</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Client</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Product</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Status</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Delivery</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt text-right">Value</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              ["s-1", "s-2", "s-3", "s-4"].map((k) => (
                <TableRow key={k}>
                  <TableCell colSpan={7}>
                    <div className="h-6 bg-zinc-100 animate-pulse" />
                  </TableCell>
                </TableRow>
              ))
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-zinc-500">
                  No orders match your filters.
                </TableCell>
              </TableRow>
            ) : (
              orders.map((o) => (
                <TableRow key={o.order_id} className="hover:bg-zinc-50 border-zinc-200">
                  <TableCell className="font-mono-mt text-sm">{o.order_number}</TableCell>
                  <TableCell>
                    <div className="font-medium text-zinc-900">{o.client_name}</div>
                    <div className="mt-tiny text-zinc-400">{o.client_email}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-zinc-900">{o.product_name}</div>
                    <div className="mt-tiny text-zinc-400">Qty {o.quantity}</div>
                  </TableCell>
                  <TableCell><StatusChip status={o.current_status} /></TableCell>
                  <TableCell className="text-sm">{formatDate(o.expected_delivery)}</TableCell>
                  <TableCell className="font-mono-mt font-semibold text-right">{formatCurrency(o.total_price)}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      <ShareTrackingButton orderId={o.order_id} orderNumber={o.order_number} compact />
                      <Link
                        to={`/admin/orders/${o.order_id}`}
                        data-testid={`admin-manage-${o.order_number}`}
                        className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white px-3 py-1.5 text-xs font-medium transition-colors"
                      >
                        <PencilSimple size={14} /> Manage
                      </Link>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Layout>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`p-6 border ${accent ? "bg-[#0047AB] text-white border-[#0047AB]" : "bg-white border-zinc-200"}`}>
      <div className={`mt-tiny ${accent ? "text-white/60" : "text-zinc-500"}`}>{label}</div>
      <div className={`font-display text-3xl font-black mt-2 ${accent ? "text-white" : "text-zinc-900"}`}>{value}</div>
    </div>
  );
}


function generatePassword() {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

function NewClientDialog() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", company: "", password: generatePassword() });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const reset = () => {
    setForm({ email: "", name: "", company: "", password: generatePassword() });
    setCreated(null);
  };

  const submit = async () => {
    if (!form.email || !form.name || !form.password) {
      toast.error("Fill email, name and password");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/create-client", form);
      setCreated({ ...form });
      toast.success("Client account created");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to create client";
      toast.error(typeof detail === "string" ? detail : "Failed to create client");
    } finally {
      setBusy(false);
    }
  };

  const copyCredentials = () => {
    if (!created) return;
    const text = `MaterialTrack access\nURL: ${window.location.origin}\nEmail: ${created.email}\nPassword: ${created.password}`;
    navigator.clipboard.writeText(text);
    toast.success("Credentials copied — paste into the client's message");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <button
        onClick={() => setOpen(true)}
        data-testid="admin-new-client-button"
        className="inline-flex items-center gap-2 border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white px-5 py-3 font-medium transition-colors"
      >
        <UserPlus size={18} /> New Client
      </button>
      <DialogContent className="rounded-none border-zinc-200 max-w-md" data-testid="new-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Create client account</DialogTitle>
          <DialogDescription>
            Set a password now, then message the credentials to the client out-of-band.
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="space-y-4">
            <div className="border-l-2 border-[#0047AB] bg-blue-50/50 pl-4 py-3">
              <div className="mt-tiny text-zinc-500">Client credentials</div>
              <div className="font-mono-mt text-sm mt-2 space-y-1">
                <div><span className="text-zinc-500">Email:</span> {created.email}</div>
                <div><span className="text-zinc-500">Password:</span> {created.password}</div>
              </div>
            </div>
            <button
              onClick={copyCredentials}
              data-testid="copy-credentials-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2.5 font-medium transition-colors"
            >
              <Copy size={16} /> Copy full credentials
            </button>
            <button
              onClick={() => { reset(); setOpen(false); }}
              className="w-full border border-zinc-200 hover:border-zinc-900 px-4 py-2.5 text-sm"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Email</div>
              <Input
                type="email"
                data-testid="new-client-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-none h-11 border-zinc-200"
                placeholder="ops@contoso.com"
              />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Contact name</div>
              <Input
                data-testid="new-client-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none h-11 border-zinc-200"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Company</div>
              <Input
                data-testid="new-client-company-input"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="rounded-none h-11 border-zinc-200"
                placeholder="Contoso Construction Ltd."
              />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5 flex items-center justify-between">
                <span>Password</span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, password: generatePassword() })}
                  className="text-[11px] text-[#0047AB] hover:underline"
                  data-testid="regen-password-button"
                >
                  regenerate
                </button>
              </div>
              <Input
                data-testid="new-client-password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-none h-11 border-zinc-200 font-mono-mt"
              />
            </div>
          </div>
        )}

        {!created && (
          <DialogFooter>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="create-client-submit-button"
              className="bg-[#FF5A1F] hover:bg-[#E04814] text-white px-6 py-2.5 font-medium transition-colors disabled:opacity-60"
            >
              {busy ? "Creating..." : "Create client"}
            </button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
